import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SortIcon from '@mui/icons-material/Sort';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import type { GridSortModel } from '@mui/x-data-grid';
import type { FC } from 'react';
import { useState } from 'react';
import { NAVY, RADIUS_ITEM, SURFACE_TINT } from 'styles/tokens';

const SORT_OPTIONS = [
  { field: 'lastMessageDate', label: 'Último mensaje' },
  { field: 'windowClosing', label: 'Ventana por cerrar' },
  { field: 'firstName', label: 'Nombre' },
  { field: 'lastCRMUpdate', label: 'Subida CRM' },
  { field: 'source', label: 'Origen' },
  { field: 'id', label: 'ID' },
] as const;

interface SortDropdownProps {
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
}

const SortDropdown: FC<SortDropdownProps> = ({ sortModel, onSortModelChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const currentSort = sortModel[0];

  const currentLabel = currentSort
    ? (SORT_OPTIONS.find((o) => o.field === currentSort.field)?.label ?? currentSort.field)
    : null;

  const handleSelect = (field: string) => {
    onSortModelChange([{ field, sort: 'desc' }]);
    setAnchorEl(null);
  };

  const handleToggleDirection = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentSort) return;
    onSortModelChange([{ field: currentSort.field, sort: currentSort.sort === 'desc' ? 'asc' : 'desc' }]);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: RADIUS_ITEM,
          bgcolor: SURFACE_TINT,
          overflow: 'hidden',
          transition: 'background-color 0.15s ease',
        }}
      >
        {/* Field selector — opens dropdown */}
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.06)' },
          }}
        >
          <SortIcon sx={{ fontSize: '1rem', color: `${NAVY}80` }} />
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8rem', color: NAVY, fontWeight: 500 }}>
            {currentLabel ?? 'Ordenar'}
          </Typography>
        </Box>

        {/* Direction toggle — independent button, only shown when a field is selected */}
        {currentSort && (
          <Box
            onClick={handleToggleDirection}
            aria-label={currentSort.sort === 'desc' ? 'Ordenar ascendente' : 'Ordenar descendente'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 0.75,
              py: 0.5,
              cursor: 'pointer',
              borderLeft: `1px solid rgba(21, 24, 58, 0.08)`,
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.06)' },
            }}
          >
            {currentSort.sort === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: '0.85rem', color: NAVY }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: '0.85rem', color: NAVY }} />
            )}
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: RADIUS_ITEM,
              mt: 0.5,
              boxShadow: '0px 8px 24px rgba(21, 24, 58, 0.12)',
            },
          },
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.field}
            selected={currentSort?.field === option.field}
            onClick={() => handleSelect(option.field)}
            sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY }}
          >
            {option.label}
            {currentSort?.field === option.field && currentSort.sort === 'asc' && (
              <ArrowUpwardIcon sx={{ fontSize: '0.85rem', ml: 1, color: NAVY }} />
            )}
            {currentSort?.field === option.field && currentSort.sort === 'desc' && (
              <ArrowDownwardIcon sx={{ fontSize: '0.85rem', ml: 1, color: NAVY }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SortDropdown;
